import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import CardHeader from "../../Common/CardHeader/CardHeader";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import axiosInstance from "../../../redux/api";
import { formatDate, formatDateTime } from "../../Common/Utils/dateUtils";
import { getStatusText } from "../../Common/Utils/statusUtils";
import {
  formatPurchaseSourceLabel,
  formatRegistrationSourceLabel,
  getMembershipStatusClass,
  getMembershipTermLabel,
  getUserStatusClass,
} from "../../Common/Utils/adminViewUtils";
import Customer360Filter from "./Customer360Filter";
import "../../Common/Styles/Listing.css";
import "../../Common/Styles/AdminEnhancements.css";

const INITIAL_PARAMS = {
  page: 1,
  per_page: 10,
  search_key: "",
  customer_status: "",
  onboarding: "",
  phase2_status: "",
  created_start_date: "",
  created_end_date: "",
  last_login_start_date: "",
  last_login_end_date: "",
  has_membership: "",
  membership_status: "",
  membership_id: "",
  membership_term: "",
  purchase_source: "",
  membership_start_date: "",
  membership_end_date: "",
  membership_expiry_window_days: "",
  invoice_type: "",
  usage_state: "",
  testing_account_filter: "hide",
  sort_by: "created_on",
  sort_order: "desc",
};

const formatRemainingUsage = (usage) => {
  const total = Number(usage?.total ?? 0);
  if (total === 10000) return "Unlimited";
  return Number(usage?.left ?? 0);
};

const getMembershipStatusText = (status) => {
  if (Number(status) === 1) return "Active";
  if (Number(status) === 2) return "Expired";
  if (Number(status) === 0) return "Pending";
  return "--";
};

const getPhase2StatusClass = (statusGroup) => {
  if (statusGroup === "integrated") return "success";
  if (statusGroup === "in_progress") return "warning";
  if (statusGroup === "failed") return "danger";
  return "";
};

const Customer360List = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [modals, setModals] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customer360Data, setCustomer360Data] = useState({
    users: [],
    summary: null,
    pagination_meta: null,
  });

  useEffect(() => {
    let active = true;

    const fetchCustomer360 = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.post("user/get_all_with_membership", params);
        if (!active) return;
        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setCustomer360Data({
            users: payload?.users || [],
            summary: payload?.summary || null,
            pagination_meta: payload?.pagination_meta || null,
          });
        } else {
          setCustomer360Data({ users: [], summary: null, pagination_meta: null });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setCustomer360Data({ users: [], summary: null, pagination_meta: null });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchCustomer360();
    return () => {
      active = false;
    };
  }, [params]);

  const users = useMemo(() => customer360Data?.users || [], [customer360Data?.users]);
  const backendSummary = customer360Data?.summary;
  const paginationMeta = customer360Data?.pagination_meta;

  const summary = useMemo(() => {
    const withMembershipFallback = users.filter((item) => item?.latest_membership).length;
    const activeCustomersFallback = users.filter((item) => Number(item?.status) === 1).length;
    const expiringSoonFallback = users.filter((item) => {
      const endDate = item?.latest_membership?.end_date;
      if (!endDate) return false;
      const today = new Date();
      const expiryDate = new Date(endDate);
      const diff = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 30;
    }).length;
    const hasBackendSummary = Boolean(backendSummary);
    const totalCustomers =
      backendSummary?.customers ??
      paginationMeta?.total ??
      users.length;
    const acrossAllText = hasBackendSummary ? "Across all matching filters" : "Current page view";

    return [
      {
        label: "Customers",
        value: totalCustomers,
        subtext: acrossAllText,
      },
      {
        label: "With membership",
        value: backendSummary?.with_membership ?? withMembershipFallback,
        subtext: hasBackendSummary ? "Latest membership linked, all pages" : "Latest membership linked",
      },
      {
        label: "Active customers",
        value: backendSummary?.active_customers ?? activeCustomersFallback,
        subtext: hasBackendSummary ? "Customer status active, all pages" : "Customer status active",
      },
      {
        label: "Expiring in 30 days",
        value: backendSummary?.expiring_30_days ?? expiringSoonFallback,
        subtext: hasBackendSummary
          ? "Latest membership end date, all pages"
          : "Based on latest membership end date",
      },
    ];
  }, [backendSummary, paginationMeta, users]);

  return (
    <Card className="py-3">
      <Customer360Filter
        modal={modals}
        setModal={setModals}
        params={params}
        setParams={setParams}
      />
      <CardHeader
        headerTitle="Customer 360"
        params={params}
        setParams={setParams}
        modals={modals}
        setModals={setModals}
        searchPlaceholder="Search by company, first name, last name, user ID, email, or mobile"
      />
      <div className="px-3">
        <div className="admin-toolbar">
          <div className="admin-toolbar-note">
            Unified customer profile + latest membership + usage in a single table.
          </div>
        </div>
        <div className="admin-summary-grid">
          {summary.map((item) => (
            <div className="admin-summary-card" key={item.label}>
              <div className="admin-summary-label">{item.label}</div>
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-subtext">{item.subtext}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="table-sec px-3">
          <div className="customer360-table-wrap">
            <table className="table border-table-sec">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Registration Source</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Account Type</th>
                  <th>Phase 2 Status</th>
                  <th>Membership</th>
                  <th>Term</th>
                  <th>Membership Source</th>
                  <th>Membership Status</th>
                  <th>Start</th>
                  <th>Expiry</th>
                  <th>Left Simplified</th>
                  <th>Left TAX</th>
                  <th>Left Purchase</th>
                  <th>Left Credit</th>
                  <th>Left Debit</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((item, index) => {
                    const membership = item?.latest_membership;
                    const phase2Status = item?.phase2_status || {};
                    const phase2StatusClass = getPhase2StatusClass(phase2Status?.status_group);
                    const usage = membership?.usage || {};
                    const pageOffset =
                      ((customer360Data?.pagination_meta?.current_page || params.page || 1) - 1) *
                      (customer360Data?.pagination_meta?.per_page || params.per_page || 10);
                    return (
                      <tr key={item?.id || index}>
                        <td>{pageOffset + index + 1}</td>
                        <td>
                          <div>{item?.company_name || "--"}</div>
                          <div className="admin-inline-note">{item?.full_name || "--"}</div>
                          <div className="admin-inline-note">User ID: {item?.id || "--"}</div>
                        </td>
                        <td>
                          <div>{item?.email_id || "--"}</div>
                          <div className="admin-inline-note">
                            {item?.country_code || ""} {item?.mobile_number || "--"}
                          </div>
                        </td>
                        <td>
                          <span className="admin-chip">
                            {formatRegistrationSourceLabel(
                              item?.registration_source_label || item?.registration_source
                            )}
                          </span>
                        </td>
                        <td>{formatDate(item?.created_on)}</td>
                        <td>{formatDateTime(item?.last_login_date)}</td>
                        <td>
                          <span className={`admin-chip admin-chip--${getUserStatusClass(item?.status)}`}>
                            {getStatusText(item?.status)}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-chip${item?.is_testing_account ? " admin-chip--warning" : " admin-chip--success"}`}>
                            {item?.is_testing_account ? "Testing Account" : "Customer"}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-chip${phase2StatusClass ? ` admin-chip--${phase2StatusClass}` : ""}`}>
                            {phase2Status?.status_label || "Not started"}
                          </span>
                        </td>
                        <td>{membership?.membership_title || "--"}</td>
                        <td>
                          <span className="admin-chip admin-chip--info">
                            {membership ? getMembershipTermLabel(membership) : "--"}
                          </span>
                        </td>
                        <td>
                          <span className="admin-chip">
                            {formatPurchaseSourceLabel(membership?.purchase_source_label)}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-chip admin-chip--${getMembershipStatusClass(membership?.status)}`}>
                            {getMembershipStatusText(membership?.status)}
                          </span>
                        </td>
                        <td>{formatDate(membership?.start_date)}</td>
                        <td>{formatDate(membership?.end_date)}</td>
                        <td>{formatRemainingUsage(usage?.simplified)}</td>
                        <td>{formatRemainingUsage(usage?.vat)}</td>
                        <td>{formatRemainingUsage(usage?.purchase)}</td>
                        <td>{formatRemainingUsage(usage?.credit)}</td>
                        <td>{formatRemainingUsage(usage?.debit)}</td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <button
                              className="border-button"
                              onClick={() => navigate(`/user/update/${item?.id}`)}
                            >
                              <span>Update</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="21" className="text-center">No Customer Found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <CommonPagination
            params={params}
            setParams={setParams}
            pagination_meta={customer360Data?.pagination_meta}
          />
        </CardBody>
      )}
    </Card>
  );
};

export default Customer360List;
